import { NodeSingular } from 'cytoscape';
import { cy } from '../components/DiagramCanvas';
import { currentDiagram } from '../components/App';
import { EdgeType } from '../model/edge-model';
import { MMNode, MMRoot, NodeType } from '../model/node-model';
import { eleCounter } from './elementCounter';
import { addEdge, addNode } from './general';

const NODE_TYPES: NodeType[] = [NodeType.Object, NodeType.Process, NodeType.State];
const EDGE_TYPES = Object.values(EdgeType);
const DEFAULT_EXTENT = { x1: -500, x2: 500, y1: -500, y2: 500 };

const randomItem = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

const randomPosition = () => {
  const extent = cy?.extent();
  if (!extent) {
    return { x: 0, y: 0 };
  }

  const x1 = isFinite(extent.x1) ? extent.x1 : DEFAULT_EXTENT.x1;
  const x2 = isFinite(extent.x2) ? extent.x2 : DEFAULT_EXTENT.x2;
  const y1 = isFinite(extent.y1) ? extent.y1 : DEFAULT_EXTENT.y1;
  const y2 = isFinite(extent.y2) ? extent.y2 : DEFAULT_EXTENT.y2;

  const width = x2 - x1 || DEFAULT_EXTENT.x2 - DEFAULT_EXTENT.x1;
  const height = y2 - y1 || DEFAULT_EXTENT.y2 - DEFAULT_EXTENT.y1;

  return {
    x: x1 + Math.random() * width,
    y: y1 + Math.random() * height,
  };
};

const resolveParent = (nodeType: NodeType, attachableNodes: MMNode[], objectNodes: MMNode[], root: MMNode | MMRoot) => {
  if (nodeType === NodeType.State && objectNodes.length) {
    const parent = randomItem(objectNodes);
    return { parentRef: parent, parentId: parent.id };
  }

  if (nodeType === NodeType.State && !objectNodes.length) {
    return { parentRef: root, parentId: '' };
  }

  if (attachableNodes.length && Math.random() < 0.3) {
    const parent = randomItem(attachableNodes);
    return { parentRef: parent, parentId: parent.id };
  }

  return { parentRef: root, parentId: '' };
};

const createNode = (type: NodeType, parentRef: MMNode | MMRoot, parentId: string): MMNode | null => {
  const id = eleCounter.value;
  const labelPrefix = type.charAt(0).toUpperCase() + type.slice(1);
  const data = {
    id,
    label: `${labelPrefix} ${id}`,
    type,
    parent: parentId,
    MMRef: null,
  };
  addNode(cy, data, randomPosition(), parentRef);
  return data.MMRef as MMNode | null;
};

const createEdge = (source: MMNode, target: MMNode) => {
  const id = eleCounter.value;
  const data = {
    id,
    label: '',
    type: randomItem(EDGE_TYPES),
    MMRef: null,
    source,
    target,
  };
  addEdge(cy, data);
};

export const addRandomNodesAndEdges = (count: number = 1000) => {
  if (!cy) {
    return;
  }

  const mainNode = (currentDiagram?.mainNode ?? currentDiagram) as MMNode | MMRoot;
  const newNodes: MMNode[] = [];
  const attachableNodes: MMNode[] = [];
  const objectNodes: MMNode[] = [];

  cy.nodes().forEach((node: NodeSingular) => {
    const ref = node.data('MMRef') as MMNode | undefined;
    if (!ref || ref.type === NodeType.State) {
      return;
    }
    attachableNodes.push(ref);
    if (ref.type === NodeType.Object) {
      objectNodes.push(ref);
    }
  });

  cy.startBatch();

  for (let i = 0; i < count; i++) {
    let type = randomItem(NODE_TYPES);
    if (type === NodeType.State && !objectNodes.length) {
      type = Math.random() > 0.5 ? NodeType.Object : NodeType.Process;
    }

    const { parentRef, parentId } = resolveParent(type, attachableNodes, objectNodes, mainNode);
    const nodeRef = createNode(type, parentRef, parentId);

    if (!nodeRef) {
      continue;
    }

    newNodes.push(nodeRef);
    if (nodeRef.type !== NodeType.State) {
      attachableNodes.push(nodeRef);
    }
    if (nodeRef.type === NodeType.Object) {
      objectNodes.push(nodeRef);
    }
  }

  for (let i = 0; i < count; i++) {
    if (newNodes.length < 2) {
      break;
    }
    const source = randomItem(newNodes);
    let target = randomItem(newNodes);
    let attempts = 0;
    while (target === source && attempts < 5) {
      target = randomItem(newNodes);
      attempts++;
    }
    if (source === target) {
      continue;
    }
    createEdge(source, target);
  }

  cy.endBatch();
};
